import { Box, Button, PasswordInput, Stack, Text } from '@mantine/core';
import React, { useLayoutEffect, useState } from 'react';

const STORAGE_KEY = 'urbantxt_org_site_preview_unlocked';

function isStagingModeEnabled(): boolean {
  const v = process.env.GATSBY_STAGING_MODE?.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

type SitePreviewGateProps = {
  children: React.ReactNode;
};

function SitePreviewGate({ children }: SitePreviewGateProps) {
  const stagingMode = isStagingModeEnabled();
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    if (!stagingMode) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(STORAGE_KEY) === '1') {
      setUnlocked(true);
    }
  }, [stagingMode]);

  if (!stagingMode) {
    return children;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const apiUrl = process.env.GATSBY_API_URL || '';
    if (!apiUrl) {
      setError('Site configuration error. Please try again later.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/exploring-tech/site-preview/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (response.ok) {
        localStorage.setItem(STORAGE_KEY, '1');
        setUnlocked(true);
        setPassword('');
        return;
      }
      if (response.status === 401) {
        setError('Incorrect password.');
        return;
      }
      setError('Unable to verify. Please try again.');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) {
    return children;
  }

  return (
    <>
      <Box
        component="div"
        pos="fixed"
        inset={0}
        bg="rgba(0, 0, 0, 0.65)"
        style={{ zIndex: 100000 }}
      />
      <Box
        component="form"
        onSubmit={handleSubmit}
        pos="fixed"
        top="50%"
        left="50%"
        w="100%"
        maw={400}
        mx="md"
        p="xl"
        bg="var(--mantine-color-body)"
        bd="1px solid var(--mantine-color-default-border)"
        style={{
          zIndex: 100001,
          transform: 'translate(-50%, -50%)',
          borderRadius: 'var(--mantine-radius-md)',
          boxShadow: 'var(--mantine-shadow-xl)',
        }}
      >
        <Stack gap="md">
          <Text fw={600} size="lg">
            Enter preview password
          </Text>
          <PasswordInput
            label="Password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.currentTarget.value)}
            autoComplete="current-password"
            disabled={loading}
            autoFocus
          />
          {error ? (
            <Text c="red" size="sm">
              {error}
            </Text>
          ) : null}
          <Button type="submit" loading={loading} fullWidth>
            Continue
          </Button>
        </Stack>
      </Box>
      <Box style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }} aria-hidden>
        {children}
      </Box>
    </>
  );
}

export default SitePreviewGate;
